import React, { useContext, useEffect, useState } from 'react';
import FormGroup from '@mui/material/FormGroup';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LabelIcon from '@mui/icons-material/Label';
import DescriptionIcon from '@mui/icons-material/Description';
import LaunchIcon from '@mui/icons-material/Launch';
import Divider from '@mui/material/Divider';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import useForm from '../../../../components/_hook/useForm';
import useLanguage from '../../../../components/_hook/useLanguage';
import { useUpdateProductInformationMutation } from '../../../../../graphql/mutation/product/updateProductInformationMutation';
import useNotification from '../../../../components/_hook/useNotification';
import useRouter from '../../../../components/_hook/useRouter';

import {
  useProductLazyQuery,
  useProductQuery,
} from '../../../../../graphql/query/product/useProductQuery';
import NotFound from '../../../../components/_error/NotFound';
import { ITranslationSubTypeInput } from '../../../../../graphql/type/_inputTypes/translation';
import SelectSearchClassification from '../../../../components/_select/SelectSearchClassification';
import SelectSearchBrand from '../../../../components/_select/SelectSearchBrand';
import SelectSearchTag from '../../../../components/_select/SelectSearchTag';
import Select, {
  IValue,
  IValues,
} from '../../../../components/_select/_module/Select';
import Context from '../../../../context';
import useRoutes from '../../../../components/_hook/useRoutes';
import ProductLayout from '../../../../layout/Product';
import StickyFormFooter from '../../../../components/_form/StickyFormFooter';
import { useCompanyShowcaseCategoryLazyQuery } from '../../../../../graphql/query/companyShowcaseCategory/useCompanyShowcaseCategoryQuery';
import InputTranslationTextField from '../../../../components/_input/InputTranslation/InputTranslationTextField';
import InputTranslationTextarea from '../../../../components/_input/InputTranslation/InputTranslationTextarea';
import SelectCurrency from '../../../../components/_select/SelectCurrency';
import SelectCountry from '../../../../components/_select/SelectCountry';
import useKeepitalRoutes from '../../../../components/_hook/useKeepitalRoutes';
import Helmet from '../../../../components/Helmet';
import ProductDetailDropshippedProductInfo from '../_module/ProductDetailDropshippedProductInfo';
import { useDuplicateProductsMutation } from '../../../../../graphql/mutation/product/duplicateProductsMutation';
import PRODUCT_HSCODE from '../../../../components/constant/productHsCode';
import { IKeywordClassificationSchema } from '../../../../../graphql/type/keywordClassification';
import { IKeywordBrandSchema } from '../../../../../graphql/type/keywordBrand';
import { IKeywordTagSchema } from '../../../../../graphql/type/keywordTag';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
}));

function ProductDetailInformation() {
  interface IFormValue {
    status: boolean;
    featured: boolean;
    enableDropshipping: boolean;

    importId: string;
    ordering: string;
    condition: string;
    categoryOrdering: Record<string, string>;

    name: ITranslationSubTypeInput[];
    introduction: ITranslationSubTypeInput[];
    unit: ITranslationSubTypeInput[];
    hsCode: string;

    origin: string;
    referenceUrl: string;

    seoKeywords: string;

    classifications: IValues;
    brands: IValues;
    tags: IValues;

    hideDisplayPrice: boolean;
    currency: string;
    costPrice: number;
    basePrice: number;
    finalPrice: number;
    regularPrice: number;
    overridePrice: number;
  }

  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { company } = useContext(Context);
  const { docID: productId } = params;
  const { getText } = useLanguage();
  const { path: keepitalPath } = useKeepitalRoutes();
  const productFragment = `
            _id
            status
            slug
            featured
            dropshipping {
              enable
            }
            import_id
            ordering
            condition
            categoryOrdering
            isDropshippedProduct
            name {
              la
              text
            }
            infos {
              intro {
                la
                text
              }
              unit {
                la
                text
              }
              hsCode
            }
            origin
            sameAs
            seoKeywords
            keywords {
              classifications
              classificationsData {
                _id
                name
              }
              brands
              brandsData {
                _id
                name
              }
              tags
              tagsData {
                _id
                name
              }
            }
            prices {
              isHidePrice
              currency
              cost
              base
              final
              regular
              override
            }
  `;
  const [
    relatedKeywordClassifications,
    setRelatedKeywordClassifications,
  ] = useState<IKeywordClassificationSchema[]>([]);
  const [relatedKeywordBrands, setRelatedKeywordBrands] = useState<
    IKeywordBrandSchema[]
  >([]);
  const [relatedKeywordTags, setRelatedKeywordTags] = useState<
    IKeywordTagSchema[]
  >([]);
  const { data, loading } = useProductQuery(productFragment, {
    variables: {
      filter: {
        companyId: company._id,
        _id: productId as string,
      },
    },
    skip: productId === 'new',
  });

  const [
    getProductRelatedKeyword,
    {
      data: productRelatedKeywordData,
      loading: isProductRelatedKeywordLoading,
    },
  ] = useProductLazyQuery(
    `
           _id
          keywords {
            classifications
            classificationsData {
              _id
              name
            }
            brands
            brandsData {
              _id
              name
            }
            tags
            tagsData {
              _id
              name
            }
          }
      `
  );

  useEffect(() => {
    if (productRelatedKeywordData && productRelatedKeywordData.products) {
      const tempProducts = productRelatedKeywordData.products.items.filter(
        Boolean
      );
      const uniqueClassificationData = tempProducts
        .reduce((acc, product) => {
          const classificationsData =
            product.keywords.classificationsData || [];

          classificationsData.forEach((classification) => {
            if (!acc.find((item) => item._id === classification._id)) {
              acc.push(classification);
            }
          });
          return acc;
        }, [] as IKeywordClassificationSchema[])
        .filter(Boolean);

      const uniqueBrandData = tempProducts
        .reduce((acc, product) => {
          const brandsData = product.keywords.brandsData || [];

          brandsData.forEach((brand) => {
            if (!acc.find((item) => item._id === brand._id)) {
              acc.push(brand);
            }
          });
          return acc;
        }, [] as IKeywordBrandSchema[])
        .filter(Boolean);
      const uniqueTagData = tempProducts
        .reduce((acc, product) => {
          const tagsData = product.keywords.tagsData || [];

          tagsData.forEach((tag) => {
            if (!acc.find((item) => item._id === tag._id)) {
              acc.push(tag);
            }
          });
          return acc;
        }, [] as IKeywordTagSchema[])
        .filter(Boolean);

      setRelatedKeywordClassifications(uniqueClassificationData);
      setRelatedKeywordBrands(uniqueBrandData);
      setRelatedKeywordTags(uniqueTagData);
    }
  }, [JSON.stringify(productRelatedKeywordData)]);

  const [
    selectingCategoryOrdering,
    setSelectingCategoryOrdering,
  ] = useState<string>('');

  const [
    getCompanyShowcaseCategories,
    {
      data: relatedCompanyShowcaseCategoryData,
      loading: isRelatedCompanyShowcaseCategoryLoading,
    },
  ] = useCompanyShowcaseCategoryLazyQuery(
    `
           _id
          company
          name {
            text
            la
          }
      `
  );

  useEffect(() => {
    if (!loading && data) {
      const product = data.products.items[0];

      if (
        product &&
        product.keywords &&
        ((product.keywords.classifications &&
          product.keywords.classifications.length) ||
          (product.keywords.brands && product.keywords.brands.length) ||
          (product.keywords.tags && product.keywords.tags.length))
      ) {
        getCompanyShowcaseCategories({
          variables: {
            filter: {
              companyId: company._id,
              anyClassificationIds: product.keywords.classifications
                ? product.keywords.classifications
                : [],
              anyBrandsIds: product.keywords.brands
                ? product.keywords.brands
                : [],
              anyTagsIds: product.keywords.tags ? product.keywords.tags : [],
            },
            limit: 100,
          },
        });
      }
    }
  }, [loading, JSON.stringify(data)]);

  function onCompleted() {
    notification.success(t('done'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateProductInformationMutation,
    { loading: isUpdatingProduct },
  ] = useUpdateProductInformationMutation(
    `updatedProduct {
				${productFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  const [duplicateProductsMutation] = useDuplicateProductsMutation(
    `createdProducts {
				_id
				company
			}`,
    {
      onCompleted: (tempData) => {
        notification.success(t('done'));
        if (
          tempData &&
          tempData.duplicateProducts &&
          tempData.duplicateProducts.createdProducts &&
          tempData.duplicateProducts.createdProducts.length &&
          tempData.duplicateProducts.createdProducts[0].company &&
          tempData.duplicateProducts.createdProducts[0]._id
        ) {
          window.open(
            path('product.information', {
              companyID: tempData.duplicateProducts.createdProducts[0].company,
              docID: tempData.duplicateProducts.createdProducts[0]._id,
            }),
            '_blank'
          );
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function duplicateProduct() {
    if (data) {
      await duplicateProductsMutation({
        variables: { _ids: [data.products.items[0]._id] },
      });
    }
  }

  async function onSubmitForm(tempValues: IFormValue) {
    if (data) {
      await updateProductInformationMutation({
        variables: {
          _id: data.products.items[0]._id,
          status: tempValues.status,
          featured: tempValues.featured,
          enableDropshipping: tempValues.enableDropshipping,

          importId: tempValues.importId,
          ordering: tempValues.ordering,
          condition: tempValues.condition,
          categoryOrdering: tempValues.categoryOrdering || {},

          name: tempValues.name,
          introduction: tempValues.introduction,
          unit: tempValues.unit,
          hsCode: tempValues.hsCode,

          origin: tempValues.origin,
          referenceUrl: tempValues.referenceUrl,

          seoKeywords: tempValues.seoKeywords
            .split(',')
            .map((item: string) => item.trim()),

          classifications: tempValues.classifications.map(
            (brand) => brand.label
          ),
          brands: tempValues.brands.map((brand) => brand.label),
          tags: tempValues.tags.map((brand) => brand.label),

          hideDisplayPrice: tempValues.hideDisplayPrice,
          currency: tempValues.currency,
          costPrice: tempValues.costPrice,
          basePrice: tempValues.basePrice,
          finalPrice: tempValues.finalPrice,
          regularPrice: tempValues.regularPrice,
          overridePrice: tempValues.overridePrice,
        },
      });
    } else {
      //   await createProductMutation({
      //     variables: {
      //       companyId: company._id,
      //       displayTypes: values.displayTypes,
      //       name: values.name,
      //       description: values.description,
      //       status: values.status,
      //       featured: values.featured,
      //       classificationMatch: values.classificationMatch,
      //       classifications: values.classifications.map(
      //         (classification) => classification.label
      //       ),
      //       brandMatch: values.brandMatch,
      //       brands: values.brands.map((brand) => brand.label),
      //       tagMatch: values.tagMatch,
      //       tags: values.tags.map((tag) => tag.label),
      //       uploadedDescriptionImageIds: values.uploadedDescriptionImageIds,
      //       parentIds: values.parentIds,
      //     },
      //   });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      status: true,
      featured: false,
      enableDropshipping: false,

      importId: '',
      ordering: 'ZZZZ',
      condition: 'new',
      categoryOrdering: {},

      name: [],
      introduction: [],
      unit: [],
      hsCode: '',

      seoKeywords: '',

      origin: '',
      referenceUrl: '',
      classifications: [],
      brands: [],
      tags: [],

      hideDisplayPrice: false,
      currency: '',
      costPrice: 0,
      basePrice: 0,
      finalPrice: 0,
      regularPrice: 0,
      overridePrice: 0,
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.name.length || !getText(tempValues.name)) {
        newErrors.name = t('__name__is_required', {
          name: t('name'),
        });
      }

      if (
        tempValues.enableDropshipping &&
        (!tempValues.currency || !tempValues.finalPrice) &&
        company.settings &&
        company.settings.enableDropshipping
      ) {
        newErrors.currency = t(
          'dropshipping_enable_but_no_sales_price_entered'
        );
        newErrors.finalPrice = t(
          'dropshipping_enable_but_no_sales_price_entered'
        );
      }

      if (
        (tempValues.costPrice ||
          tempValues.basePrice ||
          tempValues.finalPrice ||
          tempValues.overridePrice) &&
        !tempValues.currency
      ) {
        newErrors.currency = t('__name__is_required', {
          name: t('currency'),
        });
      }

      if (!tempValues.unit.length || !getText(tempValues.unit)) {
        newErrors.unit = t('__name__is_required', {
          name: t('unit'),
        });
      }

      Object.keys(newErrors).forEach((newError) =>
        notification.error(newErrors[newError])
      );

      return newErrors;
    },
  });

  function getRelatedKeyword(name?: string) {
    if (!isProductRelatedKeywordLoading) {
      getProductRelatedKeyword({
        variables: {
          search: {
            name: name || getText(values.name),
          },
          filter: {
            companyId: company._id,
          },
          useSearch: true,
          limit: 10,
        },
      });
    }
  }

  useEffect(() => {
    if (!loading && data && data.products.items.length) {
      const product = data.products.items[0];

      setValues({
        status: product.status,
        featured: product.featured,
        enableDropshipping: Boolean(
          product.dropshipping && product.dropshipping.enable
        ),

        importId: product.import_id || '',
        ordering: product.ordering || 'ZZZZ',
        condition: product.condition,
        categoryOrdering: product.categoryOrdering,

        name: product.name.map((label) => ({
          text: label.text,
          la: label.la,
        })),
        introduction:
          product.infos && product.infos.intro
            ? product.infos.intro.map((label) => ({
                text: label.text,
                la: label.la,
              }))
            : [],
        unit:
          product.infos && product.infos.unit
            ? product.infos.unit.map((label) => ({
                text: label.text,
                la: label.la,
              }))
            : [],
        hsCode:
          product.infos && product.infos.hsCode ? product.infos.hsCode : '',

        origin: product.origin,
        referenceUrl: product.sameAs,
        seoKeywords: product.seoKeywords ? product.seoKeywords.join(',') : '',
        classifications: product.keywords.classificationsData
          ? product.keywords.classificationsData.map((classification) => ({
              label: classification.name,
              value: classification._id,
            }))
          : [],
        brands: product.keywords.brandsData
          ? product.keywords.brandsData.map((brand) => ({
              label: brand.name,
              value: brand._id,
            }))
          : [],
        tags: product.keywords.tagsData
          ? product.keywords.tagsData.map((tag) => ({
              label: tag.name,
              value: tag._id,
            }))
          : [],

        hideDisplayPrice: product.prices
          ? Boolean(product.prices.isHidePrice)
          : false,
        currency:
          product.prices && product.prices.currency
            ? product.prices.currency
            : '',
        costPrice:
          product.prices && product.prices.cost ? product.prices.cost : 0,
        basePrice:
          product.prices && product.prices.base ? product.prices.base : 0,
        finalPrice:
          product.prices && product.prices.final ? product.prices.final : 0,
        regularPrice:
          product.prices && product.prices.regular ? product.prices.regular : 0,
        overridePrice:
          product.prices && product.prices.override
            ? product.prices.override
            : 0,
      });
      getRelatedKeyword(getText(product.name));
    }
  }, [loading]);

  if (productId !== 'new' && loading && productId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const product = productId && data ? data.products.items[0] : null;

  const companyShowcaseCategories = relatedCompanyShowcaseCategoryData
    ? relatedCompanyShowcaseCategoryData.companyShowcaseCategories.items
    : [];

  if (!product && productId !== 'new') {
    return <NotFound />;
  }

  if (product && product.isDropshippedProduct) {
    return <ProductDetailDropshippedProductInfo />;
  }

  return (
    <Paper square className={classes.paper}>
      <Helmet title={`${t('information')} | ${t('product')}`} />
      <Grid container spacing={2}>
        <Grid container justifyContent="space-between" spacing={1}>
          <Grid item>
            <Typography variant="h6">
              {product ? (
                <>
                  {t('update')} {getText(product.name)} {t('product')}
                </>
              ) : (
                <>
                  {t('create')} {t('product')}
                </>
              )}
            </Typography>
          </Grid>
          {product && (
            <Grid item>
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={duplicateProduct}
                startIcon={<FileCopyIcon />}
              >
                {t('duplicate')}
              </Button>
              &nbsp;&nbsp;
              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<LaunchIcon />}
                component="a"
                href={keepitalPath('company.showcase.product.index', {
                  origin: company.origin ? company.origin.toLowerCase() : '',
                  companySlug: company.slug ? company.slug : '',
                  productSlug: product.slug,
                })}
                target="_blank"
              >
                {t('view')}
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item container spacing={3}>
            <Grid item xs={12}>
              <Card className={classes.contentCard}>
                <CardContent>
                  <Grid container item xs={12} spacing={2}>
                    <Grid item xs={12} container alignItems="center">
                      <DescriptionIcon />
                      &nbsp;
                      <Typography variant="h6">
                        {t('basic_information')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormGroup
                        row
                        style={{
                          alignItems: 'flex-start',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.status}
                              onChange={() =>
                                setValue('status', !values.status)
                              }
                              color="primary"
                            />
                          }
                          label={t('publish')}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.featured}
                              onChange={() =>
                                setValue('featured', !values.featured)
                              }
                              color="primary"
                            />
                          }
                          label={t('featured')}
                        />
                        {company.settings &&
                          company.settings.enableDropshipping && (
                            <div>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.enableDropshipping}
                                    onChange={() =>
                                      setValue(
                                        'enableDropshipping',
                                        !values.enableDropshipping
                                      )
                                    }
                                    color="primary"
                                  />
                                }
                                label={t('enable_dropshipping')}
                              />
                              {product &&
                                product.dropshipping &&
                                product.dropshipping.enable &&
                                !values.enableDropshipping && (
                                  <FormHelperText error>
                                    {t(
                                      'disable_dropshipping_will_remove_all_related_dropshipping_request_and_product'
                                    )}
                                  </FormHelperText>
                                )}
                            </div>
                          )}
                      </FormGroup>
                    </Grid>
                    <Grid container item xs={12} spacing={1}>
                      <Grid item>
                        <TextField
                          label={t('import_id')}
                          value={values.importId}
                          onChange={(e) => setValue('importId', e.target.value)}
                          error={Boolean(errors.importId)}
                          helperText={errors.importId}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          label={t('ordering')}
                          value={values.ordering}
                          onChange={(e) => setValue('ordering', e.target.value)}
                          error={Boolean(errors.ordering)}
                          helperText={errors.ordering}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Select<IValue>
                          fullWidth
                          options={companyShowcaseCategories.map(
                            (companyShowcaseCategory) => ({
                              label: getText(companyShowcaseCategory.name),
                              value: companyShowcaseCategory._id,
                            })
                          )}
                          label={t('category_ordering')}
                          value={selectingCategoryOrdering}
                          onChange={(option) =>
                            setSelectingCategoryOrdering(
                              option ? option.value : ''
                            )
                          }
                        />
                      </Grid>
                      {selectingCategoryOrdering && (
                        <Grid item>
                          <TextField
                            label={`${t('category_ordering')} ${t('value')}`}
                            value={
                              values.categoryOrdering[
                                selectingCategoryOrdering
                              ] || ''
                            }
                            onChange={(e) => {
                              const newCategoryOrdering = {
                                ...values.categoryOrdering,
                              };

                              newCategoryOrdering[selectingCategoryOrdering] =
                                e.target.value;
                              setValue('categoryOrdering', newCategoryOrdering);
                            }}
                            error={Boolean(errors.ordering)}
                            helperText={errors.ordering}
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <div>
                        <Typography variant="subtitle2">
                          {t('condition')}
                        </Typography>
                        <ButtonGroup>
                          <Button
                            disableElevation
                            onClick={() => {
                              setValue('condition', 'new');
                            }}
                            variant={
                              values.condition === 'new'
                                ? 'contained'
                                : 'outlined'
                            }
                            color="primary"
                          >
                            {t('new')}
                          </Button>
                          <Button
                            disableElevation
                            onClick={() => {
                              setValue('condition', 'refurbished');
                            }}
                            variant={
                              values.condition === 'refurbished'
                                ? 'contained'
                                : 'outlined'
                            }
                            color="primary"
                          >
                            {t('refurbished')}
                          </Button>
                          <Button
                            disableElevation
                            onClick={() => {
                              setValue('condition', 'used');
                            }}
                            variant={
                              values.condition === 'used'
                                ? 'contained'
                                : 'outlined'
                            }
                            color="primary"
                          >
                            {t('used')}
                          </Button>
                        </ButtonGroup>
                      </div>
                    </Grid>
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={12}>
                        <InputTranslationTextField
                          fullWidth
                          required
                          label={t('name')}
                          values={values.name}
                          onChange={(tempValues) =>
                            setValue('name', tempValues)
                          }
                          error={Boolean(errors.name)}
                          onBlur={() => {
                            getRelatedKeyword();
                          }}
                          helperText={
                            errors && errors.name
                              ? errors.name
                              : t('seo_product_name_helper_text')
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <InputTranslationTextarea
                          variant="outlined"
                          fullWidth
                          required
                          label={t('introduction')}
                          values={values.introduction}
                          onChange={(tempValues) =>
                            setValue('introduction', tempValues)
                          }
                          error={Boolean(errors.introduction)}
                          helperText={errors.introduction}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <InputTranslationTextField
                          fullWidth
                          required
                          label={t('unit')}
                          values={values.unit}
                          onChange={(tempValues) =>
                            setValue('unit', tempValues)
                          }
                          error={Boolean(errors.unit)}
                          helperText={errors.unit}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <SelectCountry<IValue>
                          isClearable
                          fullWidth
                          label={t('origin')}
                          value={values.origin}
                          queryVariables={{
                            filter: {},
                          }}
                          onChange={(option) => {
                            setValue('origin', option ? option.value : '');
                          }}
                          error={Boolean(errors.origin)}
                          helperText={errors.origin}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Select<IValue>
                          fullWidth
                          options={PRODUCT_HSCODE}
                          label={t('hsCode')}
                          value={values.hsCode}
                          onChange={(option) =>
                            setValue('hsCode', option.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('reference_url')}
                          value={values.referenceUrl}
                          onChange={(e) =>
                            setValue('referenceUrl', e.target.value)
                          }
                          error={Boolean(errors.referenceUrl)}
                          helperText={errors.referenceUrl}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card className={classes.contentCard}>
                <CardContent>
                  <Grid container item xs={12} spacing={1}>
                    <Grid item xs={12} container alignItems="center">
                      <LabelIcon />
                      &nbsp;
                      <Typography variant="h6">
                        {t('keyword', { count: 2 })} &{' '}
                        {t('classification', { count: 2 })}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          {t('keywords_for_search_engine_optimization_seo')}
                        </Typography>
                        <TextField
                          fullWidth
                          onChange={(e) => {
                            setValue('seoKeywords', e.target.value);
                          }}
                          value={values.seoKeywords}
                          placeholder={t(
                            'insert_relevant_keywords_separate_by_comma_example_electric_forklift_used_forklift_toyota_forklift'
                          )}
                          InputLabelProps={{ shrink: true }}
                          multiline
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          {t('classification', { count: 2 })}
                        </Typography>
                        <SelectSearchClassification
                          isMulti
                          creatable
                          fullWidth
                          menuPosition="fixed"
                          onChange={(value) =>
                            setValue('classifications', value)
                          }
                          value={values.classifications}
                          error={Boolean(errors.classifications)}
                          helperText={errors.classifications}
                          placeholder={t('insert_relevant__type__', {
                            type: t('category', { count: 2 }),
                          })}
                        />
                      </Grid>
                      {relatedKeywordClassifications.length > 0 && (
                        <Grid item container spacing={0.5}>
                          {relatedKeywordClassifications
                            .slice(0, 10)
                            .map((relatedKeywordClassification) => {
                              if (
                                values.classifications.find(
                                  (classification) =>
                                    classification.value ===
                                    relatedKeywordClassification._id
                                )
                              ) {
                                return null;
                              }
                              return (
                                <Grid
                                  item
                                  key={relatedKeywordClassification._id}
                                >
                                  <Chip
                                    label={
                                      <span
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <AddIcon fontSize="small" />
                                        {relatedKeywordClassification.name}
                                      </span>
                                    }
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setValue('classifications', [
                                        ...values.classifications,
                                        {
                                          label:
                                            relatedKeywordClassification.name,
                                          value:
                                            relatedKeywordClassification._id,
                                        },
                                      ]);
                                    }}
                                  />
                                </Grid>
                              );
                            })}
                        </Grid>
                      )}
                      <Grid item xs={12} container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            {t('brand', { count: 2 })}
                          </Typography>
                          <SelectSearchBrand
                            isMulti
                            menuPosition="fixed"
                            creatable
                            fullWidth
                            onChange={(value) => setValue('brands', value)}
                            value={values.brands}
                            error={Boolean(errors.brands)}
                            helperText={errors.brands}
                            placeholder={t('insert_relevant__type__', {
                              type: t('brand', { count: 2 }),
                            })}
                          />
                        </Grid>
                        {relatedKeywordBrands.length > 0 && (
                          <Grid item container spacing={0.5}>
                            {relatedKeywordBrands
                              .slice(0, 10)
                              .map((relatedKeywordBrand) => {
                                if (
                                  values.brands.find(
                                    (brand) =>
                                      brand.value === relatedKeywordBrand._id
                                  )
                                ) {
                                  return null;
                                }
                                return (
                                  <Grid item key={relatedKeywordBrand._id}>
                                    <Chip
                                      label={
                                        <span
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <AddIcon fontSize="small" />
                                          {relatedKeywordBrand.name}
                                        </span>
                                      }
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setValue('brands', [
                                          ...values.brands,
                                          {
                                            label: relatedKeywordBrand.name,
                                            value: relatedKeywordBrand._id,
                                          },
                                        ]);
                                      }}
                                    />
                                  </Grid>
                                );
                              })}
                          </Grid>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          {t('tag', { count: 2 })} ({t('for_grouping_purpose')})
                        </Typography>
                        <SelectSearchTag
                          isMulti
                          creatable
                          menuPosition="fixed"
                          fullWidth
                          onChange={(value) => setValue('tags', value)}
                          value={values.tags}
                          error={Boolean(errors.tags)}
                          helperText={errors.tags}
                          placeholder={t(
                            'insert_tags_to_group_products_and_services_in_your_page'
                          )}
                        />
                      </Grid>
                      {relatedKeywordTags.length > 0 && (
                        <Grid item container spacing={0.5}>
                          {relatedKeywordTags
                            .slice(0, 10)
                            .map((relatedKeywordTag) => {
                              if (
                                values.tags.find(
                                  (tag) => tag.value === relatedKeywordTag._id
                                )
                              ) {
                                return null;
                              }
                              return (
                                <Grid item key={relatedKeywordTag._id}>
                                  <Chip
                                    label={
                                      <span
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <AddIcon fontSize="small" />
                                        {relatedKeywordTag.name}
                                      </span>
                                    }
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setValue('tags', [
                                        ...values.tags,
                                        {
                                          label: relatedKeywordTag.name,
                                          value: relatedKeywordTag._id,
                                        },
                                      ]);
                                    }}
                                  />
                                </Grid>
                              );
                            })}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card className={classes.contentCard}>
                <CardContent>
                  <Grid container item xs={12} spacing={1}>
                    <Grid item xs={12} container alignItems="center">
                      <AttachMoneyIcon />
                      &nbsp;
                      <Typography variant="h6">{t('pricing')}</Typography>
                    </Grid>
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={12}>
                        <FormGroup
                          row
                          style={{
                            alignItems: 'flex-end',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.hideDisplayPrice}
                                onChange={() =>
                                  setValue(
                                    'hideDisplayPrice',
                                    !values.hideDisplayPrice
                                  )
                                }
                                color="primary"
                              />
                            }
                            label={t('hide_display_price')}
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs={12}>
                        <SelectCurrency<IValue>
                          isClearable
                          fullWidth
                          menuPosition="fixed"
                          label={t('currency')}
                          value={values.currency}
                          queryVariables={{
                            filter: {},
                          }}
                          onChange={(option) => {
                            setValue('currency', option ? option.value : '');
                          }}
                          error={Boolean(errors.currency)}
                          helperText={errors.currency}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('cost_price')}
                          value={values.costPrice}
                          onChange={(e) =>
                            setValue('costPrice', parseFloat(e.target.value))
                          }
                          error={Boolean(errors.costPrice)}
                          helperText={errors.costPrice}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {values.currency}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('base_price')}
                          value={values.basePrice}
                          onChange={(e) =>
                            setValue('basePrice', parseFloat(e.target.value))
                          }
                          error={Boolean(errors.basePrice)}
                          helperText={errors.basePrice}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {values.currency}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('selling_price')}
                          value={values.finalPrice}
                          onChange={(e) =>
                            setValue('finalPrice', parseFloat(e.target.value))
                          }
                          error={Boolean(errors.finalPrice)}
                          helperText={errors.finalPrice}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {values.currency}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('regular_price')}
                          value={values.regularPrice}
                          onChange={(e) =>
                            setValue('regularPrice', parseFloat(e.target.value))
                          }
                          error={Boolean(errors.regularPrice)}
                          helperText={errors.regularPrice}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {values.currency}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('override')}
                          value={values.overridePrice}
                          onChange={(e) =>
                            setValue(
                              'overridePrice',
                              parseFloat(e.target.value)
                            )
                          }
                          error={Boolean(errors.overridePrice)}
                          helperText={errors.overridePrice}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {values.currency}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <StickyFormFooter
          onCancel={() =>
            history.push(
              path('product.list', {
                companyID: company._id,
              })
            )
          }
          onSubmit={handleSubmit}
          isSubmitting={isUpdatingProduct}
        />
      </Grid>
    </Paper>
  );
}

export default () => <ProductLayout main={<ProductDetailInformation />} />;
